// fit.ts — lossless レイアウトの純数学 (P2)。可用 box から牌寸法/扇 step/河 pitch を算出する。
// 副作用ゼロ・DOM 非依存 (vitest: environment node で直接テスト)。count→∞ で破綻しない
// (tileW→minW, step/pitch→0) ことを保証する。なぜ純関数か: 描画と計測を分離し、極限/境界を
// テストで固定するため。aspect=1.36 は theme.css の --tile-s 契約 (高さ=幅×1.36) と一致させる。
import { useLayoutEffect, useState, type RefObject } from 'react';

export const TILE_ASPECT = 1.36; // theme.css の --tile-s 高さ係数と一致 (height = width * 1.36)
export const FAN_BADGE_STEP_RATIO = 0.34; // step < natW*この比 で additive バッジ (扇が密)
export const RIVER_COLS_DEFAULT = 6; // 河は1行6牌 (既存 SeatRiver と同じ)

export type FitMode = 'natural' | 'shrunk' | 'fanned';

export interface FitRowArgs {
  containerW: number; // 利用可能幅(px)
  count: number; // 並べる牌数
  natW: number; // 自然(目標)牌幅(px) = --tile-s ベース
  gap: number; // 牌間ギャップ(px)。natural/shrunk で有効、fanned では 0 扱い
  minW: number; // 最小牌幅(px)。これ未満には縮めない → 以降は扇
}

export interface FitRowResult {
  mode: FitMode;
  tileW: number; // 実際に使う牌幅 (→ TileView size / --tile-s)
  step: number; // 隣の牌の開始位置オフセット(px)。natural/shrunk は tileW+gap, fanned は < tileW
  badge: boolean; // additive ×N を併記すべきか (fanned かつ step が密)
}

export interface FitRiverArgs {
  boxW: number; // 河ボックス幅(px)
  boxH: number; // 河ボックス高(px)
  count: number; // 河の牌数 (無制限に増えうる)
  cols?: number; // 既定 RIVER_COLS_DEFAULT(6)
  aspect?: number; // 既定 TILE_ASPECT(1.36)
  minW?: number; // 既定 4 (これ未満には縮めない最終下限)
}

export interface FitRiverResult {
  tileW: number; // 河牌幅 (→ TileView size)
  tileH: number; // tileW*aspect (描画基準高さ)
  rowPitch: number; // 行送り(px)。tileH を超えない時は縦オーバーラップ(lossless)
  cols: number; // 実効列数
  rows: number; // ceil(count/cols)
  vOverlap: boolean; // rowPitch < tileH (行が縦に重なっている)
}

// fitRow — 1次元行を natural → shrunk → fanned の順に lossless 解決する。
// なぜこの順か: ① natural(全牌が目標寸法で収まる) → ② shrunk(gap を残し全幅へ縮める) →
// ③ fanned(下限でも溢れる時のみ重ね合わせ)。どの段でも牌は1枚も消えない。
// ∞極限: count→∞ で fanned の step=(W-minW)/(count-1)→0、tileW=minW 固定、総幅→W で有界。
export function fitRow({ containerW, count, natW, gap, minW }: FitRowArgs): FitRowResult {
  const lo = Math.max(1, minW);
  const nat = Math.max(lo, natW);
  // count<=1 は並べる隙間が無いので natural 安全値。count<=0 も描画なしだが安全に返す。
  if (count <= 1) return { mode: 'natural', tileW: nat, step: nat + gap, badge: false };
  // 空間ゼロ/負でも牌を消さない: 下限寸法で原点に重ねる (step=0)。
  if (containerW <= 0) return { mode: 'fanned', tileW: lo, step: 0, badge: true };

  const need = count * nat + (count - 1) * gap;
  if (need <= containerW) return { mode: 'natural', tileW: nat, step: nat + gap, badge: false };

  // shrunk: gap を残して全幅へ収める牌幅。
  const w = (containerW - (count - 1) * gap) / count;
  if (w >= lo) return { mode: 'shrunk', tileW: w, step: w + gap, badge: false };

  // fanned: 下限を割る時のみ。全 count 牌を [0, containerW] に収める扇 step。
  const raw = (containerW - lo) / (count - 1);
  const step = Math.max(0, Math.min(raw, lo)); // 0..minW に有界 (重なり量は牌幅未満)
  return { mode: 'fanned', tileW: lo, step, badge: step < nat * FAN_BADGE_STEP_RATIO };
}

// fitRiver — 河 (2D)。cols 固定で、行を縦オーバーラップさせて boxH に必ず収める。
// 横は cols 固定なので tileW は count に依らず安定 (河が横へ無限に伸びない)。
// 縦は rows=ceil(count/cols) を rowPitch=min(tileH, boxH/rows) で送る → rows*rowPitch<=boxH。
// ∞極限: count→∞ で rows→∞、rowPitch=boxH/rows→0、全牌が boxH 内・tileW 不変で1枚も消えない。
export function fitRiver({
  boxW,
  boxH,
  count,
  cols = RIVER_COLS_DEFAULT,
  aspect = TILE_ASPECT,
  minW = 4,
}: FitRiverArgs): FitRiverResult {
  const c = Math.max(1, Math.floor(cols));
  const lo = Math.max(1, minW);
  // 下限のみ clamp (河は小さく保つ。上限を絞りたい呼び出し側は boxW を絞る)。
  const tileW = Math.max(lo, boxW > 0 ? boxW / c : lo);
  const tileH = tileW * aspect;
  const n = Math.max(0, count);
  const rows = Math.max(1, Math.ceil(n / c));
  // boxH が来ていない (計測前) 時は tileH をそのまま使う安全値。
  const rowPitch = boxH > 0 ? Math.min(tileH, boxH / rows) : tileH;
  return { tileW, tileH, rowPitch, cols: c, rows, vOverlap: rowPitch < tileH - 0.001 };
}

// useElementSize — ref 要素の border-box を ResizeObserver で購読し {w,h} を返す。
// rAF で間引き (連続リサイズの過剰 setState を防ぐ)。SSR/初回/observer 不在は {0,0} で安全
// (fitRow/fitRiver は containerW<=0 を縮退処理するので 0 でも牌は消えない)。unmount 安全。
export function useElementSize<T extends HTMLElement>(ref: RefObject<T | null>): { w: number; h: number } {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    let disposed = false;
    let raf = 0;
    let ro: ResizeObserver | null = null;
    // 値が前回と同じ (0.5px 未満差) なら setState しない → rerender 抑制。
    const apply = (w: number, h: number) =>
      setSize((p) => (Math.abs(p.w - w) < 0.5 && Math.abs(p.h - h) < 0.5 ? p : { w, h }));
    // **レイアウト寸法**を読む (offsetWidth/Height)。getBoundingClientRect は CSS transform 越しに
    // 視覚寸法(回転で w/h が入れ替わる)を返すため使わない — 強制横画面(.force-landscape の rotate(90deg))
    // 下でも論理寸法(回転前=横長キャンバス)を得る。ResizeObserver の borderBoxSize も transform 免疫だが
    // offsetWidth/Height に統一して単純化する。
    const measure = (el: T) => apply(el.offsetWidth, el.offsetHeight);
    // 要素が**この effect より後に mount される**ケース (例: 親が g 未準備の間 早期 return し、
    // 後で table-surface を描画する) に対応: ref.current が null の間は rAF で待ってから observe する。
    // 旧実装は el===null で即 return し、依存が [ref] で再実行されないため永久に 0 のままだった。
    const attach = () => {
      if (disposed) return;
      const el = ref.current;
      if (!el) { raf = requestAnimationFrame(attach); return; }
      measure(el); // 即時計測 (ResizeObserver の初回コールバックを待たない)
      if (typeof ResizeObserver === 'undefined') return; // jsdom 等: 初回計測のみ
      ro = new ResizeObserver(() => {
        if (disposed || raf) return; // 1フレーム1回に間引き
        raf = requestAnimationFrame(() => {
          raf = 0;
          const cur = ref.current;
          if (!disposed && cur) measure(cur);
        });
      });
      ro.observe(el);
    };
    attach();
    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [ref]);
  return size;
}
